/**
 * MonthlyReviewWidget - Monthly review and goals
 */

import React, { useState } from 'react';
import { Target, Calendar } from 'lucide-react';
import { WidgetWrapper } from './WidgetWrapper';
import ReviewModal from '../ReviewModal';
import type { MonthlyReviewWidgetConfig } from '../../types/widgets';
import type { MonthUpdate } from '../../types';
import { useMonths } from '../../hooks/useMonths';

interface MonthlyReviewWidgetProps {
  config: MonthlyReviewWidgetConfig;
  widgetId: string;
  windowId: string;
}

export function MonthlyReviewWidget({ config, widgetId, windowId }: MonthlyReviewWidgetProps) {
  const { currentMonth, updateMonth, isLoading } = useMonths(new Date());
  const [expanded, setExpanded] = useState(false);

  const monthName = new Date().toLocaleString('default', { month: 'long', year: 'numeric' });

  const handleStartReview = () => {
    setExpanded(true);
  };

  return (
    <>
    <WidgetWrapper title={`Monthly Review - ${monthName}`} widgetId={widgetId} windowId={windowId} onHeaderClick={() => setExpanded(true)}>
      <div className="p-3 h-full overflow-auto">
        {isLoading ? (
          <div className="flex items-center justify-center h-20 text-gray-400">
            Loading...
          </div>
        ) : !currentMonth ? (
          <div className="flex flex-col items-center justify-center h-20 text-gray-400">
            <Calendar className="w-8 h-8 mb-2" />
            <p className="text-sm">No monthly review yet</p>
            <button onClick={handleStartReview} className="mt-2 text-xs text-blue-500 hover:underline">
              Start Monthly Review
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Goals */}
            {config.showGoals !== false && currentMonth.goals?.length > 0 && (
              <div>
                <h4 className="flex items-center gap-1.5 text-xs font-medium text-gray-500 mb-2">
                  <Target className="w-3.5 h-3.5" />
                  Monthly Goals
                </h4>
                <ul className="space-y-1">
                  {currentMonth.goals.map((goal, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2 flex-shrink-0" />
                      {goal}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Top Priorities */}
            {currentMonth.top_priorities?.length > 0 && (
              <div>
                <h4 className="text-xs font-medium text-gray-500 mb-2">Top Priorities</h4>
                <ol className="list-decimal list-inside space-y-1">
                  {currentMonth.top_priorities.map((priority, i) => (
                    <li key={i} className="text-sm text-gray-700 dark:text-gray-300">
                      {priority}
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {/* Reflections */}
            {config.showReflections !== false && Array.isArray(currentMonth.reflections) && currentMonth.reflections.some(e => e.response?.trim()) && (
              <div>
                <h4 className="text-xs font-medium text-gray-500 mb-2">Reflections</h4>
                <div className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 p-2 rounded">
                  {currentMonth.reflections.filter(e => e.response?.trim()).map((entry, i) => (
                    <p key={i} className="mb-1">
                      <span className="font-medium">{entry.prompt}:</span> {entry.response}
                    </p>
                  ))}
                </div>
              </div>
            )}

            {/* Review CTA */}
            {!currentMonth.is_review_completed ? (
              <button
                onClick={handleStartReview}
                className="w-full py-2 px-3 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded text-sm font-medium hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
              >
                Start Monthly Review
              </button>
            ) : (
              <button
                onClick={handleStartReview}
                className="w-full py-2 px-3 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 rounded text-xs hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
              >
                View Month Details
              </button>
            )}
          </div>
        )}
      </div>
    </WidgetWrapper>

    {expanded && currentMonth && (
      <ReviewModal
        isOpen
        onClose={() => setExpanded(false)}
        reviewType="monthly"
        periodLabel={monthName}
        reflections={currentMonth.reflections}
        goals={currentMonth.goals}
        onSave={(data) => updateMonth(currentMonth.id, { ...data, is_review_completed: false } as MonthUpdate)}
        onComplete={(data) => updateMonth(currentMonth.id, { ...data, is_review_completed: true } as MonthUpdate)}
      />
    )}
    </>
  );
}
